import React, { useEffect, useMemo, useState } from 'react';

/**
 * Waveform strip — top-of-deck horizontal bar chart. Uses a stable pseudo-random
 * seeded pattern per track title so the "wave" doesn't flicker between renders,
 * with a gold playhead marker driven by the audio element's currentTime.
 */
export const WaveformStrip = ({ audioRef, isPlaying, seed, testid }) => {
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const el = audioRef?.current;
    if (!el) return;
    const onMeta = () => setDuration(el.duration || 0);
    const onTime = () => setTime(el.currentTime || 0);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('timeupdate', onTime);
    return () => { el.removeEventListener('loadedmetadata', onMeta); el.removeEventListener('timeupdate', onTime); };
  }, [audioRef, seed]);

  const bars = useMemo(() => {
    const n = 96;
    const s = (seed || 'x').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return Array.from({ length: n }, (_, i) => {
      const x = (Math.sin((i + s) * 0.7) + Math.sin((i + s) * 0.31) + Math.sin((i + s) * 1.6)) / 3;
      return 0.28 + Math.abs(x) * 0.72;
    });
  }, [seed]);

  const progress = duration > 0 ? time / duration : 0;
  return (
    <div className="relative w-full h-16 flex items-center gap-[2px] px-3" data-testid={testid}>
      {/* Beat-grid ticks */}
      <div className="absolute inset-y-0 left-3 right-3 pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="absolute top-0 bottom-0 w-px bg-[#FFB000]/10" style={{ left: `${(i / 12) * 100}%` }} />
        ))}
      </div>
      {bars.map((b, i) => {
        const passed = i / bars.length <= progress;
        return (
          <div
            key={i}
            className={`relative flex-1 rounded-[2px] ${passed ? 'bg-[#FFB000]' : 'bg-[#FFB000]/25'}`}
            style={{ height: `${b * 100}%`, opacity: passed ? 1 : 0.55 }}
          />
        );
      })}
      {/* Playhead */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.9)] pointer-events-none"
        style={{ left: `calc(${(progress * 100).toFixed(2)}% )`, transition: isPlaying ? 'left 200ms linear' : 'none' }}
      />
    </div>
  );
};
